import React, { useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faRocket,
  faUndo,
  faBolt,
  faListCheck,
  faUsers,
  faMicrochip,
  faGear,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';
import { betaUrl } from '../../config/hosts';
import SentryDropdown from '../SentryDropdown';
import BetaTestingTab from './BetaTestingTab';
import BetaUsersTab from './BetaUsersTab';
import BetaDevicesTab from './BetaDevicesTab';
import BetaSettingsTab from './BetaSettingsTab';
import { OtaMode } from '../../utils/otaMode';

const subNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  `nav-link${isActive ? ' active' : ''}`;

const BetaProgramView: React.FC = () => {
  const [processingOTA, setProcessingOTA] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBetaOTA = async (otaMode: OtaMode) => {
    setProcessingOTA(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/devices/update-by-type`,
        { type: 'beta', otaMode },
        { withCredentials: true }
      );
      const action = otaMode === 'none' ? 'cleared' : `set to ${otaMode}`;
      setStatusMessage(`${response.data.modifiedCount} beta devices ${action} for OTA.`);
    } catch (err: any) {
      console.error('Error updating beta devices:', err);
      setError('Failed to update OTA mode for beta devices');
    } finally {
      setProcessingOTA(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="row mb-4">
        <div className="col d-flex flex-wrap align-items-start justify-content-between gap-3">
          <div>
            <h2 className="mb-1">
              <FontAwesomeIcon icon={faFlask} className="me-2 text-primary" />
              Beta
            </h2>
            <p className="text-muted mb-0">
              Plan deploy tests, then manage Beta testers and devices on the beta track.
            </p>
          </div>
          <a
            href={betaUrl('/')}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary btn-sm"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-1" />
            Open Portal
          </a>
        </div>
      </div>

      {statusMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {statusMessage}
          <button type="button" className="btn-close" onClick={() => setStatusMessage(null)} aria-label="Close" />
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="mb-1">Quick Actions</h5>
              <div className="text-muted small">Set or clear OTA mode for all beta devices.</div>
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <SentryDropdown
                environment="beta"
                className=""
                title="Open Sentry — Beta"
              />
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleBetaOTA('none')}
                disabled={processingOTA}
              >
                {processingOTA ? (
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                ) : (
                  <FontAwesomeIcon icon={faUndo} className="me-1" />
                )}
                Clear OTA
              </button>
              <button
                className="btn btn-outline-success btn-sm"
                onClick={() => handleBetaOTA('opt-in')}
                disabled={processingOTA}
              >
                {processingOTA ? (
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                ) : (
                  <FontAwesomeIcon icon={faRocket} className="me-1" />
                )}
                Deploy Opt-in
              </button>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleBetaOTA('force')}
                disabled={processingOTA}
              >
                {processingOTA ? (
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                ) : (
                  <FontAwesomeIcon icon={faBolt} className="me-1" />
                )}
                Deploy Force
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <ul className="nav nav-tabs nav-tabs-sm mb-3 flex-nowrap overflow-auto" style={{ borderBottom: '1px solid #dee2e6' }}>
          <li className="nav-item">
            <NavLink
              to="/deploy/beta"
              end
              className={subNavLinkClass}
              style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}
            >
              <FontAwesomeIcon icon={faListCheck} className="me-1" />
              Testing
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/deploy/beta/users"
              className={subNavLinkClass}
              style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}
            >
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              Users
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/deploy/beta/devices"
              className={subNavLinkClass}
              style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}
            >
              <FontAwesomeIcon icon={faMicrochip} className="me-1" />
              Devices
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink
              to="/deploy/beta/settings"
              className={subNavLinkClass}
              style={{ fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}
            >
              <FontAwesomeIcon icon={faGear} className="me-1" />
              Settings
            </NavLink>
          </li>
        </ul>
      </div>

      <Routes>
        <Route index element={<BetaTestingTab />} />
        <Route path="users" element={<BetaUsersTab />} />
        <Route path="devices" element={<BetaDevicesTab />} />
        <Route path="settings" element={<BetaSettingsTab />} />
        <Route path="*" element={<Navigate to="/deploy/beta" replace />} />
      </Routes>
    </div>
  );
};

export default BetaProgramView;
